import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

export const useGSAPScrollAnimations = () => {
  const elementRef = useRef(null);

  useEffect(() => {
    // Refresh ScrollTrigger when component mounts
    ScrollTrigger.refresh();

    // Cleanup function
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  // Fade in from bottom animation
  const fadeInUp = (element, options = {}) => {
    const defaults = {
      y: 50,
      opacity: 0,
      duration: 0.8,
      ease: "power2.out"
    };
    
    const config = { ...defaults, ...options };
    
    gsap.fromTo(element, 
      { y: config.y, opacity: 0 },
      { 
        y: 0, 
        opacity: 1, 
        duration: config.duration, 
        ease: config.ease,
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
          ...config.scrollTrigger
        }
      }
    );
  };

  // Fade in from left animation
  const fadeInLeft = (element, options = {}) => {
    const defaults = {
      x: -50,
      opacity: 0,
      duration: 0.8,
      ease: "power2.out"
    };
    
    const config = { ...defaults, ...options };
    
    gsap.fromTo(element, 
      { x: config.x, opacity: 0 },
      { 
        x: 0, 
        opacity: 1, 
        duration: config.duration, 
        ease: config.ease,
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
          ...config.scrollTrigger
        }
      }
    );
  };

  // Fade in from right animation
  const fadeInRight = (element, options = {}) => {
    const defaults = {
      x: 50,
      opacity: 0,
      duration: 0.8,
      ease: "power2.out"
    };
    
    const config = { ...defaults, ...options };
    
    gsap.fromTo(element, 
      { x: config.x, opacity: 0 },
      { 
        x: 0, 
        opacity: 1, 
        duration: config.duration, 
        ease: config.ease,
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
          ...config.scrollTrigger
        }
      }
    );
  };

  // Scale animation
  const scaleIn = (element, options = {}) => {
    const defaults = {
      scale: 0.8,
      opacity: 0,
      duration: 0.8,
      ease: "power2.out"
    };
    
    const config = { ...defaults, ...options };
    
    gsap.fromTo(element, 
      { scale: config.scale, opacity: 0 },
      { 
        scale: 1, 
        opacity: 1, 
        duration: config.duration, 
        ease: config.ease,
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
          ...config.scrollTrigger
        }
      }
    );
  };

  // Stagger animation for multiple elements
  const staggerIn = (elements, options = {}) => {
    const defaults = {
      y: 30,
      opacity: 0,
      duration: 0.6,
      stagger: 0.1,
      ease: "power2.out"
    };
    
    const config = { ...defaults, ...options };
    
    gsap.fromTo(elements, 
      { y: config.y, opacity: 0 },
      { 
        y: 0, 
        opacity: 1, 
        duration: config.duration, 
        stagger: config.stagger,
        ease: config.ease,
        scrollTrigger: {
          trigger: elements[0],
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
          ...config.scrollTrigger
        }
      }
    );
  };

  // Rotate and scale animation
  const rotateScale = (element, options = {}) => {
    const defaults = {
      rotation: 15,
      scale: 0.8,
      opacity: 0,
      duration: 1,
      ease: "back.out(1.7)"
    };
    
    const config = { ...defaults, ...options };
    
    gsap.fromTo(element, 
      { rotation: config.rotation, scale: config.scale, opacity: 0 },
      { 
        rotation: 0, 
        scale: 1, 
        opacity: 1, 
        duration: config.duration, 
        ease: config.ease,
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
          ...config.scrollTrigger
        }
      }
    );
  };

  // Parallax animation
  const parallax = (element, options = {}) => {
    const defaults = {
      yPercent: -50,
      ease: "none"
    };
    
    const config = { ...defaults, ...options };
    
    gsap.fromTo(element, 
      { yPercent: config.yPercent },
      { 
        yPercent: 50,
        ease: config.ease,
        scrollTrigger: {
          trigger: element,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
          ...config.scrollTrigger
        }
      }
    );
  };

  // Text reveal animation
  const textReveal = (element, options = {}) => {
    const defaults = {
      duration: 0.8,
      ease: "power2.out"
    };
    
    const config = { ...defaults, ...options };
    
    // Split text into characters or words
    const text = element.textContent;
    const chars = text.split('');
    element.innerHTML = chars.map(char => 
      char === ' ' ? ' ' : `<span style="display: inline-block; opacity: 0; transform: translateY(20px);">${char}</span>`
    ).join('');
    
    const spans = element.querySelectorAll('span');
    
    gsap.to(spans, {
      opacity: 1,
      y: 0,
      duration: config.duration,
      stagger: 0.03,
      ease: config.ease,
      scrollTrigger: {
        trigger: element,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse",
        ...config.scrollTrigger
      }
    });
  };

  return {
    elementRef,
    fadeInUp,
    fadeInLeft,
    fadeInRight,
    scaleIn,
    staggerIn,
    rotateScale,
    parallax,
    textReveal
  };
};

export default useGSAPScrollAnimations;
